import { BrowserRouter } from "react-router-dom";
import { useAtom } from "jotai";
import { useEffect } from "react";
import { AnimatePresence, MotionConfig, motion } from "motion/react";
import { menuOpenAtom } from "./atoms/layout.atoms";
import { LightRays } from "./components/ui/light-rays";
import { Particles } from "./components/ui/particles";
import Header from "./components/in-house/layout/header";
import Body from "./components/in-house/layout/body";
import CinematicOverlay from "./components/in-house/layout/cinematic-overlay";
import Menu from "./components/in-house/views/menu";
import { cinematicEase } from "./components/in-house/ui/reveal-text";
import { useHardwareAcceleration } from "./hooks/use-hardware-acceleration";
import { useMediaQuery } from "./hooks/use-media-query";

function AppContent() {
  const [menuOpen, setMenuOpen] = useAtom(menuOpenAtom);
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const hasHardwareAcceleration = useHardwareAcceleration();
  const useLiteGraphics =
    !isDesktop || !hasHardwareAcceleration || reduceMotion;

  useEffect(() => {
    if (!menuOpen || isDesktop) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    window.addEventListener("keydown", handleKey);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKey);
    };
  }, [isDesktop, menuOpen, setMenuOpen]);

  return (
    <div
      data-graphics-mode={useLiteGraphics ? "lite" : "full"}
      className="flex min-h-dvh w-full flex-col items-center bg-linear-to-t from-[#00222B] to-[#000811] text-white md:overflow-x-clip"
    >
      <LightRays className="fixed" length="100dvh" />
      <Particles
        className="fixed h-svh w-dvw opacity-40 md:h-dvh md:w-full"
        quantity={useLiteGraphics ? 24 : 70}
        fps={useLiteGraphics ? 30 : 60}
        maxDpr={useLiteGraphics ? 1.25 : 1.5}
        motionEnabled={!reduceMotion}
      />
      <CinematicOverlay />
      <div className="sticky top-0 z-50 flex w-full shrink-0 justify-center before:pointer-events-none before:absolute before:inset-x-0 before:-top-px before:h-32 before:bg-[linear-gradient(to_bottom,rgb(0,8,17)_0%,rgb(0,8,17)_58%,rgba(0,8,17,0)_100%)] before:content-[''] md:px-4 md:before:h-36">
        <Header />
      </div>
      <AnimatePresence>
        {menuOpen && !isDesktop && (
          <motion.div
            key="mobile-menu"
            initial={{ opacity: 0, clipPath: "inset(0 0 100% 0)" }}
            animate={{ opacity: 1, clipPath: "inset(0 0 0% 0)" }}
            exit={{ opacity: 0, clipPath: "inset(0 0 100% 0)" }}
            transition={{
              duration: reduceMotion ? 0 : 0.55,
              ease: cinematicEase,
            }}
            className="fixed inset-x-0 bottom-0 top-18 z-40 flex items-center justify-center bg-[linear-gradient(to_bottom,rgb(0,8,17),rgb(0,34,43))] px-6"
          >
            <Menu />
          </motion.div>
        )}
      </AnimatePresence>
      <Body />
    </div>
  );
}

export default function App() {
  return (
    <MotionConfig reducedMotion="user">
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </MotionConfig>
  );
}
